import { SmPlayerSmall } from '@models/SmPlayer';

// `surface` is the background the icon sits on: the goal "ball" silhouette is
// white, so it needs the dark variant on light cards (events/squads) and the
// white original on the dark header.
export function getFixtureEventIcon(type: string, surface: 'light' | 'dark' = 'light'): string | null {
  switch (type) {
    case 'GOAL':
    case 'PENALTY':
    case 'PENALTY_SHOOTOUT_GOAL':
      return surface === 'dark'
        ? 'assets/img/match-center/ball.svg'
        : 'assets/img/match-center/ball-dark.svg';
    case 'OWNGOAL':
      return 'assets/img/match-center/own-goal.svg';
    case 'YELLOWCARD':
      return 'assets/img/match-center/yellow-card.svg';
    case 'REDCARD':
    case 'YELLOWREDCARD':
    case 'VAR_CARD':
      return 'assets/img/match-center/red-card.svg';
    case 'SUBSTITUTION':
      // change.svg has a white arrow — invisible on the white fixture cards
      return 'assets/img/match-center/change-on-light.svg';
    default:
      return null; // MISSED_PENALTY, VAR, PENALTY_SHOOTOUT_MISS, unknown
  }
}

export function formatFixtureEventMinute(
  minute: number | null,
  extraMinute: number | null
): string {
  if (minute == null) return '';
  return extraMinute ? `${minute}+${extraMinute}'` : `${minute}'`;
}

/**
 * Initials + surname for match squads and events, as football24 and Flashscore do:
 * "Кай Хаверц" → "К. Хаверц". Built from fullName, the name every other page shows.
 * A one-word name (Неймар, Родріго) is shown as is.
 *
 * When fullName ends with the API's lastName, that lastName is the surname, so particles stay
 * with it ("Бруно Нуньєш де Барруш" → "Б. Нуньєш де Барруш"). Otherwise fullName is the
 * display name and differs from first + last ("Олівер Надь" vs "Нагі"), and its last word
 * is taken as the surname ("О. Надь").
 *
 * A player without an id is the API's placeholder for an unknown player ("Гравець команди …")
 * and is shown as is.
 */
export function shortPlayerName(player: SmPlayerSmall | null): string {
  const full = (player?.fullName ?? '').trim().replace(/\s+/g, ' ');
  const surname = playerSurname(player);
  if (surname === full) {
    return full;
  }
  return `${nameInitials(full.slice(0, full.length - surname.length))} ${surname}`;
}

/**
 * The surname shortPlayerName ends with, for the pitch where only one word fits:
 * "Бруно Нуньєш де Барруш" → "Нуньєш де Барруш", "Олівер Надь" → "Надь". Names
 * shortPlayerName leaves as is come back whole.
 */
export function playerSurname(player: SmPlayerSmall | null): string {
  const full = (player?.fullName ?? '').trim().replace(/\s+/g, ' ');
  const words = full.split(' ');
  if (!player?.id || words.length <= 1) {
    return full;
  }
  const last = (player.lastName ?? '').trim().replace(/\s+/g, ' ');
  if (last === full) {
    return full;
  }
  return last && full.endsWith(' ' + last) ? last : words[words.length - 1];
}

/** "Каспер Ваарст" → "К. В." */
export function nameInitials(value: string): string {
  return value
    .split(/\s+/)
    .filter(Boolean)
    .map(token => `${token[0].toUpperCase()}.`)
    .join(' ');
}
